import { MathObject } from './MathObject';

const MIN_HEIGHT = 12;
const MIN_LINE_WIDTH = 1.25;
const MAX_LINE_WIDTH = 1.75;
const PAREN_WIDTH_RATIO = 0.16;
const BRACKET_WIDTH_RATIO = 0.14;
const BAR_WIDTH_RATIO = 0.08;

export const STRETCH_DELIMITER_CHARS = ['(', ')', '[', ']', '|'] as const;

export type StretchDelimiterChar = typeof STRETCH_DELIMITER_CHARS[number];

export const isStretchDelimiterChar = (
  value: string
): value is StretchDelimiterChar =>
  (STRETCH_DELIMITER_CHARS as readonly string[]).indexOf(value) !== -1;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

// Grow delimiter geometry without letting the stroke weight balloon the way a
// giant font glyph would.
const getLineWidth = (height: number): number =>
  clamp(height * 0.03, MIN_LINE_WIDTH, MAX_LINE_WIDTH);

const getInnerWidth = (char: StretchDelimiterChar, height: number): number => {
  const ratio =
    char === '|'
      ? BAR_WIDTH_RATIO
      : char === '[' || char === ']'
      ? BRACKET_WIDTH_RATIO
      : PAREN_WIDTH_RATIO;

  const min = char === '|' ? 2 : 5;
  const max = char === '|' ? 4 : 14;

  return clamp(height * ratio, min, max);
};

export class StretchDelimiter extends MathObject {
  char: StretchDelimiterChar;

  private lineWidth = MIN_LINE_WIDTH;
  private vertToBaseline = MIN_HEIGHT / 2;

  constructor(char: StretchDelimiterChar) {
    super();

    this.char = char;
    this.t = 'stretchDelimiter';
    this.gmmName = `stretch delimiter ${char}`;

    this.setMetrics(MIN_HEIGHT, MIN_HEIGHT / 2);
  }

  setMetrics(height: number, vertToBaseline: number): void {
    const safeHeight = Math.max(MIN_HEIGHT, height);

    this.lineWidth = getLineWidth(safeHeight);
    this.vertToBaseline = clamp(
      vertToBaseline,
      this.lineWidth / 2,
      safeHeight - this.lineWidth / 2
    );

    const innerWidth = getInnerWidth(this.char, safeHeight);

    this.setAllDim(innerWidth + this.lineWidth, safeHeight);
  }

  getVertToBaseline(): number {
    return this.vertToBaseline;
  }

  getPaintedCharCount(): number {
    // Read-aloud still speaks each grouping symbol as a single displayed
    // character, so the highlight walker needs these delimiters to count as
    // one stop instead of inheriting BasicObject's leaf default of zero.
    return 1;
  }

  toXML(): string {
    return '<t>' + this.char + '</t>';
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    const halfLine = this.lineWidth / 2;
    const top = halfLine;
    const bottom = this.viewportH - halfLine;
    const left = halfLine;
    const right = this.viewportW - halfLine;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = this.fillStyle || 'black';
    ctx.lineWidth = this.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Every path stays inside the viewport so Quantity can position delimiters
    // without extra overflow padding guesses.
    if (this.char === '(') {
      const parenMiddle = this.viewportH / 2;

      ctx.moveTo(right, top);
      ctx.quadraticCurveTo(left, this.viewportH * 0.22, left, parenMiddle);
      ctx.quadraticCurveTo(left, this.viewportH * 0.78, right, bottom);
    } else if (this.char === ')') {
      const parenMiddle = this.viewportH / 2;

      ctx.moveTo(left, top);
      ctx.quadraticCurveTo(right, this.viewportH * 0.22, right, parenMiddle);
      ctx.quadraticCurveTo(right, this.viewportH * 0.78, left, bottom);
    } else if (this.char === '[') {
      ctx.moveTo(right, top);
      ctx.lineTo(left, top);
      ctx.lineTo(left, bottom);
      ctx.lineTo(right, bottom);
    } else if (this.char === ']') {
      ctx.moveTo(left, top);
      ctx.lineTo(right, top);
      ctx.lineTo(right, bottom);
      ctx.lineTo(left, bottom);
    } else {
      const center = this.viewportW / 2;

      ctx.moveTo(center, top);
      ctx.lineTo(center, bottom);
    }

    ctx.stroke();
    ctx.restore();
  }
}

export const buildStretchDelimiter = (
  char: StretchDelimiterChar
): StretchDelimiter => new StretchDelimiter(char);
